import { Box } from '@mui/material';
import { parseISO } from 'date-fns';
import { useParams } from 'react-router-dom';

import { YearbookParams } from '../..';
import { primaryMarket as crPrimaryMarket } from '../../data/cr';
import { primaryMarkets as fidcPrimaryMarket } from '../../data/fidc';
import { primaryMarket as fiiPrimaryMarket } from '../../data/fii';
import { getMarketData, MarketKey } from '../../market';
import { ChartConfig, SectionMarket } from '../SectionMarket';

type DataRow = Record<string, string | number | null | undefined>;

interface SectionData {
  intro: string[];
  charts: Array<{
    title: string;
    link?: string;
    description: string;
    data: DataRow[];
  }>;
}

interface PrimaryMarketProps {
  id: string;
}

type ChartBuilder = (
  index: number,
  charts: SectionData['charts'],
) => ChartConfig;

const sectionDataByMarket: Partial<Record<MarketKey, SectionData>> = {
  crs: crPrimaryMarket as SectionData,
  fidc: fidcPrimaryMarket as SectionData,
  fii: fiiPrimaryMarket as SectionData,
};

function buildCrsChart(
  index: number,
  charts: SectionData['charts'],
): ChartConfig {
  const chart = charts[index];
  const rows = chart.data;

  const isLineColumnChart = rows.every(
    (row) => row.montante_emitido != null && row.numero_operacoes != null,
  );

  if (isLineColumnChart) {
    return {
      type: 'line-column',
      series: [
        {
          name: 'Montante Emitido',
          type: 'column',
          data: rows.map((r) => Number(r.montante_emitido ?? 0)),
        },
        {
          name: 'Número de Operações',
          type: 'line',
          data: rows.map((r) => Number(r.numero_operacoes ?? 0)),
        },
      ],
      categories: rows.map((r) => String(r.ano)),
    };
  }

  return {
    type: 'pie',
    series: rows.map((r) => Number(r.valor ?? 0)),
    categories: rows.map((r) => String(r.categoria ?? 'Nao informado')),
  };
}

function buildFidcChart(
  index: number,
  charts: SectionData['charts'],
): ChartConfig {
  const chart = charts[index];
  const rows = chart.data;

  if (index === 0) {
    return {
      type: 'column',
      stacked: false,
      monetary: true,
      series: [
        {
          name: 'Emissoes',
          data: rows.map((r) => r['Soma de montante_emitido'] as number),
        },
      ],
      categories: rows.map((r) =>
        parseISO(r.data_ref as string)
          .getFullYear()
          .toString(),
      ),
    };
  }

  if (index === 1) {
    const dates = [...new Set(rows.map((r) => r.data_ref as string))].sort();
    const groups = [...new Set(rows.map((r) => r.condominio as string))];

    return {
      type: 'column',
      stacked: true,
      monetary: true,
      series: groups.map((group) => ({
        name: group,
        data: dates.map((date) => {
          const match = rows.find(
            (r) => r.data_ref === date && r.condominio === group,
          );

          return match ? (match['Soma de montante_emitido'] as number) : 0;
        }),
      })),
      categories: dates.map((d) =>
        parseISO(d as string)
          .getFullYear()
          .toString(),
      ),
    };
  }

  if (index === 2) {
    return {
      type: 'pie',
      series: rows.map((r) => r['Soma de montante_emitido'] as number),
      categories: rows.map((r) => r.tipo as string),
    };
  }

  return {
    type: 'pie',
    series: rows.map((r) => r['Soma de montante_emitido'] as number),
    categories: rows.map((r) => r.ativo_lastro as string),
  };
}

function buildFiiChart(
  index: number,
  charts: SectionData['charts'],
): ChartConfig {
  const chart = charts[index];
  const rows = chart.data;

  if (index === 0) {
    return {
      type: 'column',
      stacked: false,
      monetary: true,
      series: [
        {
          name: 'Emissoes',
          data: rows.map((r) => Number(r.montante_emitido ?? 0)),
        },
      ],
      categories: rows.map((r) => String(r.ano)),
    };
  }

  const categories = [...new Set(rows.map((r) => String(r.ano)))].sort();
  const groups = [...new Set(rows.map((r) => String(r.tipo_ativo)))];

  return {
    type: 'column',
    stacked: true,
    monetary: true,
    series: groups.map((group) => ({
      name: group,
      data: categories.map((year) => {
        const match = rows.find(
          (row) => String(row.ano) === year && String(row.tipo_ativo) === group,
        );

        return match ? Number(match.montante_emitido ?? 0) : 0;
      }),
    })),
    categories,
  };
}

const chartBuilderByMarket: Partial<Record<MarketKey, ChartBuilder>> = {
  crs: buildCrsChart,
  fidc: buildFidcChart,
  fii: buildFiiChart,
};

export function PrimaryMarket({ id }: PrimaryMarketProps) {
  const { market } = useParams<YearbookParams>();
  const sectionData = getMarketData(market, sectionDataByMarket);
  const chartBuilder = getMarketData(market, chartBuilderByMarket);

  if (!sectionData || !chartBuilder) {
    return <Box component="section" id={id} data-info="no-content" />;
  }

  return (
    <SectionMarket
      id={id}
      title="Mercado Primário"
      intro={sectionData.intro}
      charts={sectionData.charts}
      getChartConfig={(index) => chartBuilder(index, sectionData.charts)}
      gradientPosition="left"
    />
  );
}
